<template>
  <div class="restaurant-kitchens-page">
    <div class="page-header">
      <div class="page-header__title">
        <h1>مدیریت آشپزخانه‌ها</h1>
        <p>تعریف و مدیریت بخش‌های مختلف آشپزخانه</p>
      </div>
      <ab-ui-button variant="primary" @click="openKitchenDialog">
        <span>افزودن آشپزخانه جدید</span>
        <i class="mdi mdi-plus" aria-hidden="true" />
      </ab-ui-button>
    </div>

    <!-- summary cards -->
    <div class="summary-cards">
      <div class="summary-card summary-card--total">
        <span class="summary-card__value">{{ kitchens.length }}</span>
        <span class="summary-card__label">کل آشپزخانه‌ها</span>
      </div>
      <div class="summary-card summary-card--active">
        <span class="summary-card__value">{{ countActive }}</span>
        <span class="summary-card__label">فعال</span>
      </div>
      <div class="summary-card summary-card--inactive">
        <span class="summary-card__value">{{ countInactive }}</span>
        <span class="summary-card__label">غیر فعال</span>
      </div>
    </div>

    <!-- search -->
    <div class="search-wrap">
      <div class="search-input-wrap">
        <i class="mdi mdi-magnify" aria-hidden="true" />
        <input
          v-model="searchQuery"
          type="text"
          class="search-input"
          placeholder="جستجو در آشپزخانه‌ها..."
        />
      </div>
    </div>

    <!-- kitchen cards grid -->
    <div v-if="loadingKitchens" class="loading-message">
      <span>در حال بارگذاری آشپزخانه‌ها...</span>
    </div>
    <div v-else class="kitchens-grid">
      <ab-ui-card
        v-for="kitchen in kitchensFiltered"
        :key="kitchen.id"
        class="kitchen-card"
      >
        <div class="kitchen-card__header">
          <div class="kitchen-card__title-row">
            <span class="kitchen-card__name">{{ kitchen.name }}</span>
            <span
              class="kitchen-card__badge"
              :class="
                kitchen.isActive
                  ? 'kitchen-card__badge--active'
                  : 'kitchen-card__badge--inactive'
              "
            >
              {{ kitchen.isActive ? "فعال" : "غیر فعال" }}
            </span>
          </div>
          <div
            class="kitchen-card__icon"
            :style="{ backgroundColor: kitchen.color || '#7c4dff' }"
          >
            <i class="mdi mdi-chef-hat" aria-hidden="true" />
          </div>
        </div>
        <p v-if="kitchen.description" class="kitchen-card__desc">
          {{ kitchen.description }}
        </p>
        <div v-if="kitchen.printerIp" class="kitchen-card__printer">
          <i class="mdi mdi-printer" aria-hidden="true" />
          <span>IP چاپگر: {{ kitchen.printerIp }}</span>
        </div>
        <div class="kitchen-card__actions">
          <button
            type="button"
            class="kitchen-card__btn kitchen-card__btn--danger"
            title="حذف"
            @click="openDeleteConfirm(kitchen)"
          >
            <i class="mdi mdi-delete-outline" aria-hidden="true" />
          </button>
          <button
            type="button"
            class="kitchen-card__btn kitchen-card__btn--edit"
            title="ویرایش"
            @click="editKitchen(kitchen)"
          >
            <i class="mdi mdi-pencil-outline" aria-hidden="true" />
          </button>
          <button
            type="button"
            class="kitchen-card__toggle"
            @click="toggleActive(kitchen)"
          >
            {{ kitchen.isActive ? "غیر فعال" : "فعال" }}
          </button>
        </div>
      </ab-ui-card>
    </div>

    <!-- add / edit kitchen modal -->
    <transition name="dialog-fade">
      <div v-if="kitchenDialog" class="dialog-backdrop" @click.self="closeKitchenDialog">
        <div class="dialog dialog--wide">
          <div class="dialog__header">
            <div>
              <span class="dialog__title">{{
                editingKitchen ? "ویرایش آشپزخانه" : "افزودن آشپزخانه جدید"
              }}</span>
              <p v-if="!editingKitchen" class="dialog__subtitle">
                آشپزخانه جدید تعریف کنید
              </p>
            </div>
            <button type="button" class="dialog__close" @click="closeKitchenDialog">
              ×
            </button>
          </div>
          <div class="dialog__body">
            <ab-ui-input
              v-model="form.name"
              label="نام آشپزخانه *"
              placeholder="مثال: آشپزخانه گرم"
            />
            <ab-ui-select
              v-model="form.branchId"
              label="شعبه"
              placeholder="شعبه را انتخاب کنید"
              :items="branchItems"
            />
            <div class="form-group">
              <label class="form-label">توضیحات</label>
              <textarea
                v-model="form.description"
                class="form-textarea"
                rows="3"
                placeholder="توضیحات مختصر درباره این آشپزخانه..."
              />
            </div>
            <div class="form-group">
              <span class="form-label">رنگ</span>
              <div class="color-swatches">
                <button
                  v-for="c in colorOptions"
                  :key="c.value"
                  type="button"
                  class="color-swatch"
                  :class="{ 'color-swatch--active': form.color === c.value }"
                  :style="{ backgroundColor: c.value }"
                  :title="c.label"
                  @click="form.color = c.value"
                />
              </div>
            </div>
            <ab-ui-input
              v-model="form.printerIp"
              label="IP چاپگر (اختیاری)"
              placeholder="192.168.1.100"
            />
            <p class="form-hint">آدرس IP چاپگر این آشپزخانه برای چاپ خودکار سفارشات</p>
          </div>
          <div class="dialog__footer">
            <ab-ui-button variant="secondary" @click="closeKitchenDialog">
              <span>انصراف</span>
              <i class="mdi mdi-close" aria-hidden="true" />
            </ab-ui-button>
            <ab-ui-button variant="primary" @click="submitKitchen">
              <span>{{ editingKitchen ? "ذخیره" : "افزودن آشپزخانه" }}</span>
              <i class="mdi mdi-plus" aria-hidden="true" />
            </ab-ui-button>
          </div>
        </div>
      </div>
    </transition>

    <!-- delete confirmation modal -->
    <transition name="dialog-fade">
      <div
        v-if="deleteConfirmKitchen"
        class="dialog-backdrop"
        @click.self="closeDeleteConfirm"
      >
        <div class="confirm-dialog">
          <div class="confirm-dialog__icon">
            <i class="mdi mdi-delete-outline" aria-hidden="true" />
          </div>
          <h3 class="confirm-dialog__title">حذف آشپزخانه</h3>
          <p class="confirm-dialog__message">
            آیا از حذف «<strong>{{ deleteConfirmKitchen.name }}</strong
            >» اطمینان دارید؟
          </p>
          <p class="confirm-dialog__warning">
            <i class="mdi mdi-alert-outline" aria-hidden="true" />
            <span>این عمل قابل بازگشت نیست.</span>
          </p>
          <div class="confirm-dialog__footer">
            <ab-ui-button variant="danger" @click="doDelete">
              <span>حذف</span>
              <i class="mdi mdi-delete-outline" aria-hidden="true" />
            </ab-ui-button>
            <ab-ui-button variant="secondary" @click="closeDeleteConfirm">
              <span>انصراف</span>
              <i class="mdi mdi-close" aria-hidden="true" />
            </ab-ui-button>
          </div>
        </div>
      </div>
    </transition>

    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      top
      right
      :timeout="3500"
      style="z-index: 9999"
    >
      {{ snackbarText }}
    </v-snackbar>
  </div>
</template>

<script>
import AbUiButton from "./partial/AbUiButton.vue";
import AbUiInput from "./partial/AbUiInput.vue";
import AbUiCard from "./partial/AbUiCard.vue";
import AbUiSelect from "./partial/AbUiSelect.vue";
import toastMixin from '@/Mixins/toastMixin'

const COLOR_OPTIONS = [
  { value: "#4caf50", label: "سبز" },
  { value: "#ffeb3b", label: "زرد" },
  { value: "#ff9800", label: "نارنجی" },
  { value: "#f44336", label: "قرمز" },
  { value: "#9e9e9e", label: "خاکستری" },
  { value: "#e91e63", label: "صورتی" },
  { value: "#7c4dff", label: "بنفش" },
  { value: "#2196f3", label: "آبی" },
];

let kitchenIdCounter = 1;

export default {
  name: "RestaurantKitchensPage",
  mixins: [toastMixin],
  components: {
    AbUiButton,
    AbUiCard,
    AbUiInput,
    AbUiSelect,
  },
  data() {
    return {
      searchQuery: "",
      kitchens: [],
      branches: [],
      loadingKitchens: false,
      kitchenDialog: false,
      editingKitchen: null,
      form: {
        name: "",
        branchId: null,
        description: "",
        color: "#7c4dff",
        printerIp: "",
      },
      colorOptions: COLOR_OPTIONS,
      deleteConfirmKitchen: null,
    };
  },
  computed: {
    branchItems() {
      return this.branches.map((b) => ({
        value: b.id,
        text: b.name || "بدون نام",
      }));
    },
    kitchensFiltered() {
      const q = (this.searchQuery || "").trim().toLowerCase();
      if (!q) return this.kitchens;
      return this.kitchens.filter((k) => {
        return (
          (k.name && k.name.toLowerCase().includes(q)) ||
          (k.description && k.description.toLowerCase().includes(q))
        );
      });
    },
    countActive() {
      return this.kitchens.filter((k) => k.isActive).length;
    },
    countInactive() {
      return this.kitchens.filter((k) => !k.isActive).length;
    },
  },
  async created() {
    await this.fetchBranches();
    await this.fetchKitchens();
  },
  methods: {
    async fetchBranches() {
      if (!this.$axios || !this.$endpoint) return;
      try {
        const res = await this.$axios.get(this.$endpoint + "branch/list", {
          params: { type: "all", options: { itemsPerPage: 1000 }, page: 1 },
        });
        const raw = res.data?.data || [];
        this.branches = raw.map((item) => ({ ...item, name: item?.name || "بدون نام" }));
      } catch (err) {
        if (this.serverErrorHandeler) this.serverErrorHandeler(err);
      }
    },
    async fetchKitchens() {
      if (!this.$axios || !this.$endpoint) {
        this.kitchens = this.getMockKitchens();
        return;
      }
      this.loadingKitchens = true;
      try {
        const res = await this.$axios.get(this.$endpoint + "restaurant/kitchens", {
          params: {
            options: { page: 1, itemsPerPage: 1000 },
            search: null,
          },
        });
        const raw = Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data)
          ? res.data
          : [];
        this.kitchens = raw.map((item) => ({
          id: item.id,
          branchId: item.branch_id ?? item.branchId ?? null,
          name: item.name,
          description: item.description || null,
          color: item.color || "#7c4dff",
          printerIp: item.printer_ip ?? item.printerIp ?? null,
          isActive: Boolean(item.is_active ?? item.isActive ?? true),
        }));
      } catch (err) {
        if (this.serverErrorHandeler) this.serverErrorHandeler(err);
        this.kitchens = this.getMockKitchens();
      } finally {
        this.loadingKitchens = false;
      }
    },
    getMockKitchens() {
      return [
        {
          id: kitchenIdCounter++,
          name: "آشپزخانه گرم",
          branchId: 1,
          description: "غذاهای گرم و اصلی",
          color: "#f44336",
          printerIp: "192.168.1.100",
          isActive: true,
        },
        {
          id: kitchenIdCounter++,
          name: "آشپزخانه سرد",
          branchId: 1,
          description: "سالادها و پیش غذاها",
          color: "#2196f3",
          printerIp: "192.168.1.101",
          isActive: true,
        },
        {
          id: kitchenIdCounter++,
          name: "بار",
          branchId: 1,
          description: "نوشیدنی‌ها و آبمیوه‌ها",
          color: "#7c4dff",
          printerIp: "192.168.1.102",
          isActive: true,
        },
        {
          id: kitchenIdCounter++,
          name: "قنادی",
          branchId: 1,
          description: "دسرها و شیرینی‌ها",
          color: "#e91e63",
          printerIp: "192.168.1.103",
          isActive: true,
        },
      ];
    },
    openKitchenDialog() {
      this.editingKitchen = null;
      this.form = {
        name: "",
        branchId: this.branches[0]?.id ?? null,
        description: "",
        color: "#7c4dff",
        printerIp: "",
      };
      this.kitchenDialog = true;
    },
    editKitchen(kitchen) {
      this.editingKitchen = kitchen;
      this.form = {
        name: kitchen.name,
        branchId: kitchen.branchId,
        description: kitchen.description || "",
        color: kitchen.color || "#7c4dff",
        printerIp: kitchen.printerIp || "",
      };
      this.kitchenDialog = true;
    },
    closeKitchenDialog() {
      this.kitchenDialog = false;
      this.editingKitchen = null;
    },
    async submitKitchen() {
      if (!this.form.name.trim()) {
        alert("نام آشپزخانه را وارد کنید.");
        return;
      }
      if (!this.$axios || !this.$endpoint) {
        alert("اتصال به سرور برقرار نیست.");
        return;
      }
      const payload = {
        branch_id: this.form.branchId,
        name: this.form.name.trim(),
        description: (this.form.description || "").trim() || null,
        color: this.form.color,
        printer_ip: (this.form.printerIp || "").trim() || null,
      };
      try {
        if (this.editingKitchen && this.editingKitchen.id) {
          const res = await this.$axios.put(
            this.$endpoint + `restaurant/kitchens/${this.editingKitchen.id}`,
            payload
          );
          const updated = res.data?.data ?? res.data ?? {};
          Object.assign(this.editingKitchen, {
            id: updated.id ?? this.editingKitchen.id,
            branchId: updated.branch_id ?? updated.branchId ?? this.form.branchId,
            name: updated.name ?? payload.name,
            description: updated.description ?? payload.description ?? null,
            color: updated.color ?? payload.color,
            printerIp: updated.printer_ip ?? updated.printerIp ?? payload.printer_ip,
            isActive: Boolean(
              updated.is_active ?? updated.isActive ?? this.editingKitchen.isActive
            ),
          });
        } else {
          const res = await this.$axios.post(
            this.$endpoint + "restaurant/kitchens",
            payload
          );
          const created = res.data?.data ?? res.data ?? {};
          this.kitchens.push({
            id: created.id ?? kitchenIdCounter++,
            branchId: created.branch_id ?? created.branchId ?? this.form.branchId,
            name: created.name ?? payload.name,
            description: created.description ?? payload.description ?? null,
            color: created.color ?? payload.color,
            printerIp: created.printer_ip ?? created.printerIp ?? payload.printer_ip,
            isActive: Boolean(created.is_active ?? created.isActive ?? true),
          });
        }
        const wasEdit = !!(this.editingKitchen && this.editingKitchen.id);
        this.closeKitchenDialog();
        this.showToast(
          wasEdit ? "آشپزخانه با موفقیت ویرایش شد" : "آشپزخانه با موفقیت اضافه شد"
        );
      } catch (err) {
        if (this.serverErrorHandeler) this.serverErrorHandeler(err);
        this.showToast("خطا در ذخیره آشپزخانه", "error");
      }
    },
    async toggleActive(kitchen) {
      if (!this.$axios || !this.$endpoint) return;
      const next = !kitchen.isActive;
      const prev = kitchen.isActive;
      kitchen.isActive = next;
      try {
        await this.$axios.patch(
          this.$endpoint + `restaurant/kitchens/${kitchen.id}/toggle`,
          { is_active: next }
        );
        this.showToast(next ? "آشپزخانه فعال شد" : "آشپزخانه غیرفعال شد");
      } catch (err) {
        kitchen.isActive = prev;
        if (this.serverErrorHandeler) this.serverErrorHandeler(err);
        this.showToast("خطا در تغییر وضعیت", "error");
      }
    },
    openDeleteConfirm(kitchen) {
      this.deleteConfirmKitchen = kitchen;
    },
    closeDeleteConfirm() {
      this.deleteConfirmKitchen = null;
    },
    async doDelete() {
      const kitchen = this.deleteConfirmKitchen;
      if (!kitchen) return;
      this.closeDeleteConfirm();
      if (!this.$axios || !this.$endpoint) {
        this.kitchens = this.kitchens.filter((k) => k.id !== kitchen.id);
        return;
      }
      const prev = [...this.kitchens];
      this.kitchens = this.kitchens.filter((k) => k.id !== kitchen.id);
      try {
        await this.$axios.delete(this.$endpoint + `restaurant/kitchens/${kitchen.id}`);
        this.showToast("آشپزخانه با موفقیت حذف شد");
      } catch (err) {
        this.kitchens = prev;
        if (this.serverErrorHandeler) this.serverErrorHandeler(err);
        this.showToast("خطا در حذف آشپزخانه", "error");
      }
    },
  },
};
</script>

<style scoped>
.restaurant-kitchens-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  background-color: #ffffff;
  border-radius: 5px;
  min-height: calc(100vh - 80px - 24px);
  height: 100%;
  box-sizing: border-box;
}

.restaurant-kitchens-page :deep(.ab-ui-btn) {
  border-radius: 8px;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-header__title h1 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.page-header__title p {
  margin: 4px 0 0;
  font-size: 12px;
  color: #777;
}

.summary-cards {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.summary-card {
  flex: 1;
  min-width: 100px;
  border-radius: 8px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.summary-card__value {
  font-size: 20px;
  font-weight: 600;
}

.summary-card__label {
  font-size: 12px;
  opacity: 0.9;
}

.summary-card--total {
  background-color: #fff;
  color: #333;
  border: 1px solid #eee;
}

.summary-card--active {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.summary-card--inactive {
  background-color: #f5f5f5;
  color: #616161;
}

.loading-message {
  padding: 24px;
  text-align: center;
  color: #666;
  font-size: 14px;
}

.search-wrap {
  max-width: 360px;
}

.search-input-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #fff;
}

.search-input-wrap .mdi {
  font-size: 18px;
  color: #999;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 13px;
  background: transparent;
}

.kitchens-grid {
  flex: 1 1 0;
  min-height: 0;
  overflow: auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 14px;
  align-content: start;
}

.kitchen-card {
  position: relative;
  padding: 14px;
}

.kitchen-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}

.kitchen-card__title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.kitchen-card__name {
  font-size: 15px;
  font-weight: 600;
}

.kitchen-card__badge {
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 500;
}

.kitchen-card__badge--active {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.kitchen-card__badge--inactive {
  background-color: #f5f5f5;
  color: #616161;
}

.kitchen-card__icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 18px;
}

.kitchen-card__desc {
  font-size: 12px;
  color: #666;
  margin: 0 0 8px;
  line-height: 1.4;
}

.kitchen-card__printer {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #555;
  margin-bottom: 12px;
}

.kitchen-card__printer .mdi {
  font-size: 16px;
  color: #777;
}

.kitchen-card__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.kitchen-card__btn {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: #f5f5f5;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: background 0.15s;
}

.kitchen-card__btn--danger:hover {
  background: #ffebee;
  color: #c62828;
}
.kitchen-card__btn--edit:hover {
  background: #e3f2fd;
  color: #1565c0;
}

.kitchen-card__toggle {
  padding: 4px 12px;
  border-radius: 8px;
  border: 1px solid #ddd;
  background: #fff;
  font-size: 12px;
  cursor: pointer;
  margin-right: auto;
}

.kitchen-card__toggle:hover {
  background: #f5f5f5;
}

/* انیمیشن باز و بسته شدن دیالوگ */
.dialog-fade-enter-active,
.dialog-fade-leave-active {
  transition: opacity 0.2s ease;
}
.dialog-fade-enter-active .dialog,
.dialog-fade-enter-active .confirm-dialog,
.dialog-fade-leave-active .dialog,
.dialog-fade-leave-active .confirm-dialog {
  transition: transform 0.2s ease, opacity 0.2s ease;
}
.dialog-fade-enter,
.dialog-fade-leave-to {
  opacity: 0;
}
.dialog-fade-enter .dialog,
.dialog-fade-enter .confirm-dialog,
.dialog-fade-leave-to .dialog,
.dialog-fade-leave-to .confirm-dialog {
  transform: scale(0.96);
  opacity: 0;
}

.dialog-backdrop {
  position: fixed;
  inset: 0;
  background-color: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.dialog {
  direction: rtl;
  background-color: #fff;
  border-radius: 16px;
  width: 480px;
  max-width: calc(100% - 32px);
  padding: 16px 18px 14px;
  box-shadow: 0 12px 36px rgba(15, 23, 42, 0.2);
}

.dialog--wide {
  width: 520px;
}

.dialog__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 12px;
}

.dialog__title {
  font-size: 14px;
  font-weight: 600;
}

.dialog__subtitle {
  font-size: 12px;
  color: #777;
  margin: 4px 0 0;
}

.dialog__close {
  border: none;
  background: transparent;
  font-size: 18px;
  cursor: pointer;
}

.dialog__body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.form-label {
  font-size: 12px;
  color: #555;
}

.form-textarea {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  resize: vertical;
  min-height: 64px;
}

.form-textarea:focus {
  outline: none;
  border-color: var(--v-theme-primary, #1976d2);
}

.color-swatches {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}

.color-swatch {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 2px solid transparent;
  cursor: pointer;
  transition: transform 0.1s, box-shadow 0.15s;
}

.color-swatch:hover {
  transform: scale(1.05);
}

.color-swatch--active {
  border-color: #333;
  box-shadow: 0 0 0 2px #fff;
}

.form-hint {
  font-size: 11px;
  color: #999;
  margin: -4px 0 0;
}

.dialog__footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 14px;
}

/* delete confirmation modal */
.confirm-dialog {
  direction: rtl;
  background-color: #fff;
  border-radius: 16px;
  width: 400px;
  max-width: calc(100% - 32px);
  padding: 24px;
  box-shadow: 0 12px 36px rgba(15, 23, 42, 0.2);
  text-align: center;
}

.confirm-dialog__icon {
  width: 56px;
  height: 56px;
  margin: 0 auto 16px;
  border-radius: 50%;
  background-color: #ffebee;
  color: #c62828;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
}

.confirm-dialog__title {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.confirm-dialog__message {
  margin: 0 0 12px;
  font-size: 14px;
  line-height: 1.6;
  color: #555;
}

.confirm-dialog__message strong {
  color: #333;
}

.confirm-dialog__warning {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0 0 24px;
  padding: 10px 14px;
  font-size: 12px;
  line-height: 1.5;
  color: #e65100;
  background-color: #fff3e0;
  border-radius: 8px;
  border: 1px solid #ffe0b2;
}

.confirm-dialog__warning .mdi {
  font-size: 18px;
  flex-shrink: 0;
}

.confirm-dialog__footer {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.confirm-dialog__footer .ab-ui-btn {
  min-width: 100px;
}
</style>
